import { LoadingOutlined } from "@ant-design/icons";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useEffect, useState } from "react";
import { Button, Col, Input, Row, Select } from "antd";

import { ICountry, IJobGrade, IModal, IShortTermPlan, IPlanType } from "@types";
import Modal from "@components/Modal";
import { showSuccessPopup } from "@utils";
import {
  ErrorServices,
  useCreateShortTermPlanMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useFetchShortTermPlanTypesQuery,
  useUpdateShortTermPlanMutation,
} from "@services";
import { useTypedSelector } from "@hooks";
import CustomModal from "@/components/customModel";

const { Option } = Select;

interface IFormValues {
  name: string;
  plan_type_id: null | number;
  country_ids: number[];
  grades: string[];
}

interface IAddShortTermPlan extends IModal {
  selectedShortTermPlan: IShortTermPlan | null;
  setSelectedShortTermPlan: React.Dispatch<
    React.SetStateAction<IShortTermPlan | null>
  >;
}

const AddShortTermPlan: FC<IAddShortTermPlan> = ({
  isVisible,
  setIsVisible,
  selectedShortTermPlan,
  setSelectedShortTermPlan,
}) => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const [isGlobal, setIsGlobal] = useState(false);
  const [isAllGrades, setIsAllGrades] = useState(false);
  const { data: planTypesData, isLoading: isFetchingTypes } =
    useFetchShortTermPlanTypesQuery(null);
  const { data: planTypes } = planTypesData || {};
  const { data: jobGradesData, isLoading: isFetchingGrades } =
    useFetchAllCompanyJobGradesQuery({ company_id });
  const { data: jobGrades } = jobGradesData || {};
  const { data: countriesData, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });
  const { data: countries } = countriesData || {};
  const [createShortTermPlan, { isLoading: isCreating }] =
    useCreateShortTermPlanMutation();
  const [updateShortTermPlan, { isLoading: isUpdating }] =
    useUpdateShortTermPlanMutation();
  const [shortTermPlan, setShortTermPlan] = useState<IFormValues>({
    name: "",
    plan_type_id: null,
    country_ids: [],
    grades: [],
  });

  useEffect(() => {
    if (selectedShortTermPlan) {
      const { grades, is_all_grade, is_global, name, countries, type } =
        selectedShortTermPlan;
      setShortTermPlan({
        grades,
        name,
        plan_type_id: type?.id,
        country_ids: countries?.map(({ id }) => id),
      });
      setIsGlobal(is_global);
      setIsAllGrades(is_all_grade);
    }
    return () => {
      setSelectedShortTermPlan(null);
    };
  }, [selectedShortTermPlan, setSelectedShortTermPlan]);

  const handleSubmit = async () => {
    try {
      const { grades, country_ids } = shortTermPlan;
      const body = {
        ...shortTermPlan,
        grades: isAllGrades ? [] : grades,
        country_ids: isGlobal ? [] : country_ids,
      };
      if (selectedShortTermPlan) {
        await editShortTermPlan(body);
      } else {
        await addShortTermPlan(body);
      }
      setIsVisible(false);
      showSuccessPopup({
        title: `Short Term Incentive Plan ${
          selectedShortTermPlan ? "Updated!" : "Created!"
        }`,
        desc: `You have successfully ${
          selectedShortTermPlan ? "updated the" : "created new"
        } short term plan.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
      console.log(error);
    }
  };

  const addShortTermPlan = async (body: IFormValues) =>
    await createShortTermPlan({ company_id, body }).unwrap();

  const editShortTermPlan = async (body: IFormValues) =>
    await updateShortTermPlan({
      company_id,
      id: selectedShortTermPlan?.id,
      body,
    }).unwrap();

  return (
    <CustomModal
      width="w-6/12"
      footer={
        <>
          <button
            className="bg-themeGrey text-themeBlack font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>
          <button
            className={`bg-themeBlue text-white font-medium text-lg py-2 px-14 rounded-md ${
              isCreating || isUpdating ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={!shortTermPlan?.name || !shortTermPlan?.plan_type_id}
            onClick={handleSubmit}
          >
            {isCreating || isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : selectedShortTermPlan ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
      title={`${selectedShortTermPlan ? "Update" : "Create"} a STI Plan`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Plan Name
            </label>
            <Input
              className={`w-full shadow-sm my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
              value={shortTermPlan?.name}
              onChange={e =>
                setShortTermPlan(prev => ({ ...prev, name: e?.target.value }))
              }
              size="large"
              placeholder="Enter plan name here..."
            />
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Plan Type
            </label>
            <Select
              className="custom-select"
              value={shortTermPlan?.plan_type_id || undefined}
              loading={isFetchingTypes}
              size="large"
              showSearch={false}
              showArrow={true}
              placeholder="Select plan type from here..."
              onChange={plan_type_id =>
                setShortTermPlan(prev => ({
                  ...prev,
                  plan_type_id,
                }))
              }
            >
              {planTypes?.map(({ id, name }: IPlanType) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Grades
            </label>
            <Select
              className="custom-select"
              disabled={isAllGrades}
              loading={isFetchingGrades}
              mode="multiple"
              showArrow={true}
              size="large"
              showSearch={false}
              placeholder="Select grades from here..."
              value={shortTermPlan?.grades}
              onChange={grades =>
                setShortTermPlan(prev => ({ ...prev, grades }))
              }
            >
              {jobGrades?.map(({ grade, id }: IJobGrade) => (
                <Option key={id} value={grade}>
                  {grade}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={11}>
            <label className="font-medium text-themeInputTitleColor text-lg leading-10 mt-4 ">
              Countries
            </label>
            <Select
              className="custom-select"
              disabled={isGlobal}
              loading={isFetchingCountries}
              mode="multiple"
              size="large"
              showSearch={true}
              showArrow={true}
              optionFilterProp="children"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={
                selectedShortTermPlan && isFetchingCountries
                  ? undefined
                  : shortTermPlan?.country_ids
              }
              onChange={country_ids =>
                setShortTermPlan(prev => ({ ...prev, country_ids }))
              }
              placeholder="Select country from here..."
            >
              {countries?.map(({ id, name }: ICountry) => (
                <Option key={id} value={id}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <div className="flex flex-row  items-center -mt-16">
          <Checkbox
            checked={isAllGrades}
            onChange={event => setIsAllGrades(event.target.checked)}
            className="mt-32"
          >
            <span className={"font-medium text-lg text-themeInputTitleColor"}>
              All Grades
            </span>
          </Checkbox>
          <Checkbox
            checked={isGlobal}
            onChange={event => setIsGlobal(event.target.checked)}
            className="mt-32"
          >
            <span className={"font-medium text-lg text-themeInputTitleColor"}>
              Global
            </span>
          </Checkbox>
        </div>
      </>
    </CustomModal>
  );
};

export default AddShortTermPlan;
